import { zodResolver } from '@hookform/resolvers/zod'
import { omit, set } from 'lodash'
import { FieldErrors, Resolver } from 'react-hook-form'
import { base } from 'viem/chains'
import { z } from 'zod'

import { SWAP_ROUTERS } from '~/components/forms/swap/utils'
import {
  accountSchema,
  baseTransactionSchema,
  multiRecipientSchema,
  relayTokenAmountSchema,
  tokenAmountSchema,
} from '~/components/smart-accounts/schemas'
import {
  BASE_USDC_ADDRESS,
  MINIMUM_GAS_TOP_UP_AMOUNT_USD,
  MINIMUM_OFFRAMP_AMOUNT,
  SPLITS_GAS_RECIPIENT,
} from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import { ContactSendRule, EarnAction, IAddress } from '~/types'
import { Address } from '~/utils/address'
import { isAllowedBySendRules } from '~/utils/contactSendRules'
import { getVaultAssetSymbol, isVaultToken } from '~/utils/vault'

const swapDataSchema = z.object({
  sellToken: tokenAmountSchema.optional(),
  sellTokenUSD: z.string().optional(),
  swapRouter: z.enum(SWAP_ROUTERS).optional(),
  providerTransactionId: z.string().optional(),
  settlementRouter: z.enum(SWAP_ROUTERS).optional(),
  earnToken: tokenAmountSchema
    .and(z.object({ action: z.nativeEnum(EarnAction) }))
    .optional(), // If sell token is a vault/position, earn token tracks underlying assets
  slippage: z.string().optional(),
})

export type SwapDataFormValues = z.infer<typeof swapDataSchema>

// Shared so the schema refinement and transferFormResolver never drift.
const offrampMinimumMessage = (isEurc: boolean) =>
  `Offramp amount must be at least ${isEurc ? '€' : '$'}${MINIMUM_OFFRAMP_AMOUNT}`

export const transferSchema = multiRecipientSchema
  .and(
    z.object({
      sender: accountSchema,
      transferToken: relayTokenAmountSchema,
      swapData: swapDataSchema.optional(),
      isUSDInput: z.boolean().optional(),
      isDirectionReversed: z.boolean().optional(),
      /** Fiat the bank receives on an offramp. `recipients.0.amount` holds the
       * grossed-up token amount actually sent. */
      offrampRecipientAmount: z.string().optional(),
      /** Synced from external hook — true when offramp amount < $10/€10. */
      offrampBelowMinimum: z.boolean().optional(),
      /** Synced from external hook — true when transfer token is EURC. */
      offrampIsEurc: z.boolean().optional(),
    }),
  )
  .and(baseTransactionSchema)
  .superRefine((data, ctx) => {
    if (
      data.swapData?.sellToken &&
      isVaultToken(
        data.swapData.sellToken.chainId,
        data.swapData.sellToken.token,
      ) &&
      !data.swapData.earnToken
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['swapData', 'earnToken'],
        message: 'Earn token is required for vault token transfers',
      })
    }

    if (data.recipients) {
      const recipientAddresses = data.recipients.map((r) => r.receiver.address)
      const uniqueRecipientAddresses = [...new Set(recipientAddresses)]
      if (uniqueRecipientAddresses.length !== recipientAddresses.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recipients'],
          message: 'Cannot have duplicate recipients',
        })
      }
    }

    if (data.offrampBelowMinimum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['offrampRecipientAmount'],
        message: offrampMinimumMessage(!!data.offrampIsEurc),
      })
    }
  })
  // The three offramp fields drive validation only; they never reach a
  // transaction, so strip them on the way out.
  .transform((data) =>
    omit(data, [
      'offrampRecipientAmount',
      'offrampBelowMinimum',
      'offrampIsEurc',
    ]),
  )

export type TransferFormValues = z.input<typeof transferSchema>

/** The org's bank accounts, injected into the resolver via RHF context. */
export type TransferResolverContext = {
  bankAccounts: { address: string; isEurc: boolean }[]
  /** The send rules of restricted contacts, keyed by lowercase address. An
   * empty map means no restrictions. */
  contactSendRules: Record<string, ContactSendRule[]>
  /** The org's smart account addresses, lowercase. Earn tokens can only be
   * sent to these. Undefined while the accounts are still loading, which
   * skips the check rather than rejecting every recipient. */
  smartAccountAddresses: string[] | undefined
}

const isEarnDestination = ({
  token,
  chainId,
}: {
  token: string | undefined
  chainId: SupportedChainId | undefined
}): boolean =>
  !!token &&
  !!chainId &&
  Address.validate(token) &&
  !!isVaultToken(chainId, token)

const isOrgAccount = ({
  address,
  smartAccountAddresses,
}: {
  address: string
  smartAccountAddresses: string[]
}): boolean => smartAccountAddresses.includes(address.toLowerCase())

const addEarnRecipientError = ({
  errors,
  index,
  token,
  chainId,
}: {
  errors: FieldErrors<TransferFormValues>
  index: number
  token: IAddress
  chainId: SupportedChainId
}) => {
  const assetSymbol = getVaultAssetSymbol(chainId, token)
  set(errors, `recipients.${index}.receiver.address`, {
    type: 'earnRecipient',
    message: `Earn ${assetSymbol} can only be sent to accounts in this workspace`,
  })
}

/**
 * Wraps zodResolver to add cross-field errors that can't live in the schema:
 * recipient/sender VM-compatibility and the offramp minimum. Both must run on
 * every validation pass — a schema refinement (zod skips refinements when a base
 * field fails to parse) or an effect-synced flag made them flicker.
 */
export const transferFormResolver: Resolver<
  TransferFormValues,
  TransferResolverContext
> = async (values, context, options) => {
  const result = await zodResolver(transferSchema)(values, context, options)
  const errors = { ...result.errors }
  let added = false

  // Each recipient must match the transfer token's VM type, and the sender the
  // swap sell token's.
  const tokenAddress = values.transferToken?.token
  if (tokenAddress && Address.validate(tokenAddress)) {
    values.recipients?.forEach((recipient, index) => {
      const recipientAddress = recipient?.receiver?.address
      if (
        recipientAddress &&
        Address.validate(recipientAddress) &&
        !Address.compatible(recipientAddress, tokenAddress)
      ) {
        set(errors, `recipients.${index}.receiver.address`, {
          type: 'compatibility',
          message:
            'Recipient address is not compatible with the selected token',
        })
        added = true
      }
    })
  }

  // A backstop for contact send rules. The pickers filter and the hook
  // clears, so this check trips only on edge paths such as a URL prefill or
  // a race.
  const transferChainId = values.transferToken?.chainId
  if (tokenAddress && transferChainId && Address.validate(tokenAddress)) {
    values.recipients?.forEach((recipient, index) => {
      const recipientAddress = recipient?.receiver?.address
      const sendRules = recipientAddress
        ? context?.contactSendRules[recipientAddress.toLowerCase()]
        : undefined
      if (
        sendRules &&
        !isAllowedBySendRules({
          sendRules,
          tokenAddress,
          chainId: transferChainId,
        })
      ) {
        set(errors, `recipients.${index}.receiver.address`, {
          type: 'sendRule',
          message: "This recipient can't receive the selected token",
        })
        added = true
      }
    })
  }

  // Undefined smart accounts are still loading, so the Earn check waits.
  const smartAccountAddresses = context?.smartAccountAddresses
  if (
    smartAccountAddresses &&
    tokenAddress &&
    transferChainId &&
    isEarnDestination({ token: tokenAddress, chainId: transferChainId })
  ) {
    values.recipients?.forEach((recipient, index) => {
      const recipientAddress = recipient?.receiver?.address
      if (
        recipientAddress &&
        Address.validate(recipientAddress) &&
        !isOrgAccount({ address: recipientAddress, smartAccountAddresses })
      ) {
        // A vault token can only be sent to the org's own accounts.
        addEarnRecipientError({
          errors,
          index,
          token: tokenAddress,
          chainId: transferChainId,
        })
        added = true
      }
    })
  }

  const sellTokenAddress = values.swapData?.sellToken?.token
  if (
    values.sender &&
    sellTokenAddress &&
    Address.validate(values.sender) &&
    Address.validate(sellTokenAddress) &&
    !Address.compatible(values.sender, sellTokenAddress)
  ) {
    set(errors, 'sender', {
      type: 'compatibility',
      message: 'Sender is not compatible with the selected swap token',
    })
    added = true
  }

  // The minimum applies to the fiat the bank receives, which the user types
  // directly. Empty stays quiet — the schema already gates submission.
  const receiver = values.recipients?.[0]?.receiver?.address
  const bankAccount =
    receiver &&
    context?.bankAccounts.find((account) =>
      Address.equal(account.address, receiver),
    )
  const rawAmount = values.offrampRecipientAmount
  if (bankAccount && rawAmount) {
    const amount = Number(rawAmount)
    if (!Number.isNaN(amount) && amount < MINIMUM_OFFRAMP_AMOUNT) {
      set(errors, 'offrampRecipientAmount', {
        type: 'custom',
        message: offrampMinimumMessage(bankAccount.isEurc),
      })
      added = true
    }
  }

  // A top-up is only recognised as Base USDC to the gas recipient, so the
  // minimum applies to exactly that pair. Empty stays quiet, as above.
  const isGasTopUpToken =
    tokenAddress &&
    Address.equal(tokenAddress, BASE_USDC_ADDRESS) &&
    transferChainId === base.id
  if (isGasTopUpToken) {
    values.recipients?.forEach((recipient, index) => {
      const amount = Number(recipient?.amount)
      if (
        recipient?.amount &&
        Address.equal(recipient.receiver?.address, SPLITS_GAS_RECIPIENT) &&
        !Number.isNaN(amount) &&
        amount < MINIMUM_GAS_TOP_UP_AMOUNT_USD
      ) {
        set(errors, `recipients.${index}.amount`, {
          type: 'custom',
          message: `Gas top-ups must be at least $${MINIMUM_GAS_TOP_UP_AMOUNT_USD}`,
        })
        added = true
      }
    })
  }

  // No added errors → hand back zod's result untouched; otherwise mark invalid.
  if (!added) return result
  return { values: {}, errors }
}

export type PersistedSelectedToken = {
  token: IAddress
  chainId: SupportedChainId
}

/** Recipient with a guaranteed address, for use after form validation at submission time. */
export type ValidatedRecipient = {
  receiver: { address: IAddress; compliantPaymentRelationId?: string }
  amount: string
  usdAmount?: string
}

/** Filters recipients to only those with a defined address, narrowing the type. */
export function getValidatedRecipients(
  recipients: TransferFormValues['recipients'],
): ValidatedRecipient[] {
  return recipients.filter((r): r is ValidatedRecipient => !!r.receiver.address)
}
